import { useId } from "react";
import { Working } from "../../../../shared/ui/Working";
import { useTranslation } from "../i18n/useTranslation";
import { servedLang, translate } from "../i18n";
import type { MessageKey } from "../i18n/messages";
import type { TermsConsent } from "../features/terms/useTermsConsent";
import { PRIVACY_POLICY_URL } from "../features/terms/terms";
import { LinkedSentence, plainSentence } from "./NewTabLink";
import { retryApiRead } from "../../../../shared/lib/appCheckRecovery";

/**
 * THE TERMS CONTROL: signed-in Review (4.3), guest Review (6.1) and account
 * creation (2.1). One component so the three cannot drift.
 *
 * Counsel's shape (2026-09-29), with Legal's 18+ sentence (2026-10-14):
 *
 *   [ ] I'm 18 or older and agree to Lura's [Terms of Service].
 *   See our [Privacy Policy].
 *
 * - ONE affirmative checkbox: no second box, no birthdate, nothing bundled
 *   with it (no marketing consent).
 * - THE SENTENCE FOLLOWS THE SERVED TERMS, not the page (Legal, 2026-10-14).
 *   The person agrees to the document the link opens, so the sentence is in
 *   that document's language: a Spanish page served only English Terms shows
 *   the English sentence, marked `lang="en"`, linking the English document.
 *   The Privacy line is page copy and stays in the page's language.
 *   (Recording the displayed wording per acceptance waits for Backend's
 *   contract.)
 * - UNTICKED on arrival, and unticked again whenever the Terms on screen
 *   change or Backend refuses them (useTermsConsent). The caller keeps its
 *   primary action disabled until `consent.decision()` is non-null.
 * - The checkbox's accessible name is the agree sentence ONLY. The link inside
 *   the label adds " (opens in a new tab)" for screen readers, which would
 *   otherwise become part of the name, so the name is set from the same
 *   catalogue string with its brackets removed.
 * - The Privacy line is a separate notice, outside the label.
 * - Nothing when no Terms are published: no checkbox, nothing sent.
 * - Unreadable: frame 4.5's line and "Load terms again". After an App Check
 *   refusal that button reloads the page (shared/lib/appCheckRecovery), EXCEPT under
 *   `keepPage`: a reload would drop an attempt that may already have committed.
 */
export function TermsAgreement({
  consent,
  unavailableKey,
  disabled = false,
  keepPage = false,
}: {
  consent: TermsConsent;
  /** The 4.5 line for this screen: "for this booking" is not true of an account. */
  unavailableKey: Extract<MessageKey, "terms.unavailable.booking" | "terms.unavailable.account">;
  /** While a send is in flight the tick may not change under it. */
  disabled?: boolean;
  /**
   * The first send may have committed, and its frozen request (the same
   * idempotency key) lives only in this page. "Load terms again" must re-read
   * in place: a reload would lose the key, and the next Confirm would mint a
   * new attempt that could book twice.
   */
  keepPage?: boolean;
}) {
  const { t, locale } = useTranslation();
  const id = useId();
  const { view } = consent;

  if (view.status === "none") return null;

  if (view.status === "loading") {
    return (
      <div className="b-terms" data-testid="terms" data-terms-state="loading">
        <Working>{t("profile.loading")}</Working>
      </div>
    );
  }

  if (view.status === "unavailable") {
    return (
      <div className="b-terms" data-testid="terms" data-terms-state="unavailable">
        <div className="b-notice t-danger" role="alert" data-testid="terms-unavailable">
          <div className="b-notice-b">
            <p className="b-p">{t(unavailableKey)}</p>
          </div>
        </div>
        <button
          type="button"
          className="b-btn v-secondary is-full"
          data-testid="terms-reload"
          onClick={() => (keepPage ? consent.reload() : retryApiRead(consent.reload))}
        >
          {t("terms.reload")}
        </button>
      </div>
    );
  }

  const sentenceLocale = servedLang(view.terms.locale) ?? locale;
  const agree = translate(sentenceLocale, "terms.agree");
  return (
    <div className="b-terms" data-testid="terms" data-terms-state="published">
      <div className="b-check" lang={sentenceLocale}>
        <input
          id={id}
          type="checkbox"
          className="b-check-box"
          data-testid="terms-agree"
          checked={consent.accepted}
          disabled={disabled}
          aria-label={plainSentence(agree)}
          onChange={(event) => consent.setAccepted(event.target.checked)}
        />
        <label htmlFor={id} className="b-check-l">
          <LinkedSentence text={agree} href={view.terms.documentUrl} testId="terms-link" locale={sentenceLocale} />
        </label>
      </div>
      <p className="b-meta b-terms-privacy" data-testid="terms-privacy">
        <LinkedSentence text={t("terms.privacy")} href={PRIVACY_POLICY_URL} testId="terms-privacy-link" />
      </p>
    </div>
  );
}
